import React, { useState, useEffect } from 'react';

export default function CommunityKitchen({ lang, t }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dbStatus, setDbStatus] = useState(null);

  // Form states
  const [dishName, setDishName] = useState('');
  const [caption, setCaption] = useState('');
  const [userName, setUserName] = useState('');
  const [userState, setUserState] = useState('Tamil Nadu');
  const [portionSize, setPortionSize] = useState('3-4 Persons');
  const [rating, setRating] = useState(5);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState('image');

  const isTa = lang === 'Tamil';
  const isHi = lang === 'Hindi';
  const isTe = lang === 'Telugu';
  const isAr = lang === 'Arabic';

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/community/posts');
      const data = await res.json();
      setPosts(data.posts || []);
    } catch (e) {
      console.error('Failed to load posts:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchDbStatus = async () => {
    try {
      const res = await fetch('/api/db/status');
      const data = await res.json();
      setDbStatus(data);
    } catch (e) {
      console.warn('DB status check failed');
    }
  };

  useEffect(() => {
    fetchPosts();
    fetchDbStatus();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMediaFile(file);
    const isVid = file.type.includes('video');
    setMediaType(isVid ? 'video' : 'image');
    setMediaPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dishName.trim()) {
      alert(isTa ? 'தயவுசெய்து உணவின் பெயரை உள்ளிடவும்.' : 'Please enter the dish name.');
      return;
    }
    if (!mediaFile) {
      alert(isTa ? 'தயவுசெய்து போட்டோ அல்லது வீடியோவைத் தேர்ந்தெடுக்கவும்.' : 'Please select a photo or video.');
      return;
    }

    try {
      setUploading(true);

      // 1. Upload media
      const formData = new FormData();
      formData.append('media', mediaFile);
      const uploadRes = await fetch('/api/community/upload', {
        method: 'POST',
        body: formData
      });
      const uploadData = await uploadRes.json();

      if (!uploadData.url) {
        throw new Error(uploadData.error || 'Upload failed');
      }

      // 2. Save post to PostgreSQL
      const postPayload = {
        dish_name: dishName,
        caption: caption,
        media_type: uploadData.media_type || mediaType,
        media_url: uploadData.url,
        portion_size: portionSize,
        rating: rating,
        user_name: userName || 'Home Chef',
        state: userState
      };

      const saveRes = await fetch('/api/community/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postPayload)
      });
      const saveData = await saveRes.json();

      if (saveData.ok) {
        setIsModalOpen(false);
        setDishName('');
        setCaption('');
        setMediaFile(null);
        setMediaPreview(null);
        await fetchPosts();
        alert(isTa ? '🎉 உங்கள் சமைத்த உணவு வெற்றிகரமாக PostgreSQL-ல் சேமிக்கப்பட்டது!' : '🎉 Food post successfully saved to PostgreSQL!');
      } else {
        throw new Error(saveData.error || 'Failed to save post');
      }
    } catch (err) {
      console.error(err);
      alert('Error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="community-section" id="communitySection" style={{ padding: '40px 14px', width: '100%', maxWidth: '1240px', margin: '0 auto', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '20px', marginBottom: '32px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <span style={{ fontSize: '2rem' }}>📸</span>
            <span style={{
              background: 'linear-gradient(90deg, #10b981, #06b6d4)',
              color: '#000',
              fontWeight: '700',
              fontSize: '0.8rem',
              padding: '4px 10px',
              borderRadius: '20px',
              letterSpacing: '0.5px'
            }}>
              {dbStatus?.postgres_connected ? '🐘 DIGITALOCEAN POSTGRESQL' : '💾 CLOUD STORAGE READY'}
            </span>
          </div>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', margin: '0 0 8px 0', color: '#fff' }}>
            {isTa ? 'மக்கள் சமையல் கூடம் — உணவுப் படங்கள் & வீடியோக்கள்' :
             isHi ? 'सामुदायिक रसोई — भोजन की तस्वीरें और वीडियो' :
             isTe ? 'కమ్యూనిటీ వంటశాల — ఆహార చిత్రాలు & వీడియోలు' :
             isAr ? 'مطبخ المجتمع — صور وفيديوهات الأطباق' :
             'Community Kitchen — Photos & Videos'}
          </h2>
          <p style={{ color: '#94a3b8', margin: 0, fontSize: '1rem', maxWidth: '650px' }}>
            {isTa 
              ? 'மக்கள் சமைத்த உணவுகளின் படங்கள், வீடியோக்கள், சுவை அனுபவங்கள் மற்றும் சமையல் வரலாறு இங்கே பதிவாகும்!'
              : isHi
              ? 'घर पर बनाए गए व्यंजनों की असली तस्वीरें, वीडियो और स्वाद अनुभव यहाँ साझा करें!'
              : isTe
              ? 'గృహిణులు మరియు ఆహార ప్రియులు పంచుకున్న నిజమైన వంటల చిత్రాలు మరియు వీడియోలు!'
              : isAr
              ? 'صور وفيديوهات حقيقية وقصص طهي يشاركها عشاق الطعام من جميع أنحاء الهند!'
              : 'Real photos, videos, and kitchen stories shared by homemakers and food lovers across India!'}
          </p>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          style={{
            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 24px',
            fontSize: '1rem',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span>📸</span>
          <span>
            {isTa ? 'சமைத்ததை பகிருங்கள்' :
             isHi ? 'अपना खाना साझा करें' :
             isTe ? 'మీ వంటను పంచుకోండి' :
             isAr ? 'شارك ما طبخته' :
             'Share What You Cooked'}
          </span>
        </button>
      </div>

      {/* Feed Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
          <div className="spinner" style={{ margin: '0 auto 16px auto' }}></div>
          <p>{isTa ? 'படங்கள் ஏற்றப்படுகின்றன...' : 'Loading community posts...'}</p>
        </div>
      ) : posts.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'rgba(30, 41, 59, 0.4)',
          borderRadius: '16px',
          border: '1px dashed rgba(255, 255, 255, 0.1)'
        }}>
          <span style={{ fontSize: '3rem' }}>🍲</span>
          <h3 style={{ color: '#fff', margin: '16px 0 8px 0' }}>
            {isTa ? 'இன்னும் எந்த பதிவும் இல்லை!' : 'No dishes shared yet!'}
          </h3>
          <p style={{ color: '#94a3b8', marginBottom: '20px' }}>
            {isTa 
              ? 'நீங்கள் சமைத்த முதல் உணவின் படம் அல்லது வீடியோவை பகிர்ந்து தொடங்குங்கள்.'
              : 'Be the first to share your home cooked food photo or video with the community.'}
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            style={{
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {isTa ? 'முதல் உணவைப் பகிருங்கள்' : 'Share First Dish'}
          </button>
        </div>
      ) : (
        <div className="community-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {posts.map((post) => (
            <div
              key={post.id}
              style={{
                background: 'rgba(30, 41, 59, 0.7)',
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
            >
              {/* Media Preview (Photo or Video with 16:9 responsive aspect ratio) */}
              <div className="community-card-media" style={{ position: 'relative', width: '100%', aspectRatio: '16/10', background: '#0f172a', overflow: 'hidden' }}>
                {post.media_type === 'video' ? (
                  <video
                    src={post.media_url}
                    controls
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <img
                    src={post.media_url}
                    alt={post.dish_name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800';
                    }}
                  />
                )}
                <span style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: '#fff',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  backdropFilter: 'blur(4px)'
                }}>
                  {post.source === 'telegram' ? '🤖 Telegram Bot' : '🌐 Web App'}
                </span>
                <span style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '12px',
                  background: 'rgba(0, 0, 0, 0.75)',
                  color: '#fbbf24',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '700'
                }}>
                  {'⭐'.repeat(post.rating || 5)}
                </span>
              </div>

              {/* Post Details */}
              <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#fff', margin: 0 }}>
                    {post.dish_name}
                  </h3>
                  <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                    {post.portion_size}
                  </span>
                </div>

                {post.caption && (
                  <p style={{ color: '#cbd5e1', fontSize: '0.9rem', lineHeight: '1.5', margin: '0 0 12px 0', flex: 1 }}>
                    "{post.caption}"
                  </p>
                )}

                {/* Chef Profile Badge */}
                <div style={{
                  marginTop: 'auto',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '0.85rem'
                    }}>
                      {(post.user?.first_name || 'C')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ color: '#f8fafc', fontWeight: '600', fontSize: '0.85rem' }}>
                        {post.user?.first_name || 'Home Chef'}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                        📍 {post.user?.state || 'Tamil Nadu'}
                      </div>
                    </div>
                  </div>

                  <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
                    {post.created_at ? new Date(post.created_at).toLocaleDateString() : 'Today'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Share Cooked Food Modal */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#1e293b',
            borderRadius: '20px',
            maxWidth: '540px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '28px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.4rem', fontWeight: '800' }}>
                📸 {isTa ? 'சமைத்த உணவை பகிரவும்' : 'Share Your Cooked Food'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '1.5rem',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Media File Upload */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#cbd5e1', fontWeight: '600', marginBottom: '8px', fontSize: '0.9rem' }}>
                  {isTa ? 'உணவு போட்டோ அல்லது வீடியோ (Photo/Video) *' : 'Food Photo or Cooking Video *'}
                </label>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    background: 'rgba(15, 23, 42, 0.6)',
                    border: '1px dashed #3b82f6',
                    color: '#fff'
                  }}
                  required
                />
                {mediaPreview && (
                  <div style={{ marginTop: '12px', borderRadius: '10px', overflow: 'hidden', height: '160px', background: '#000' }}>
                    {mediaType === 'video' ? (
                      <video src={mediaPreview} controls style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <img src={mediaPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                  </div>
                )}
              </div>

              {/* Dish Name */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', color: '#cbd5e1', fontWeight: '600', marginBottom: '6px', fontSize: '0.9rem' }}>
                  {isTa ? 'உணவின் பெயர் (Dish Name) *' : 'Dish Name *'}
                </label>
                <input
                  type="text"
                  placeholder={isTa ? 'எ.கா: செட்டிநாடு சிக்கன் பிரியாணி' : 'e.g. Chettinad Chicken Biryani'}
                  value={dishName}
                  onChange={(e) => setDishName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: 'rgba(15, 23, 42, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '0.95rem'
                  }}
                  required
                />
              </div>

              {/* Chef Name & State */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label style={{ display: 'block', color: '#cbd5e1', fontWeight: '600', marginBottom: '6px', fontSize: '0.9rem' }}>
                    {isTa ? 'உங்கள் பெயர்' : 'Your Name'}
                  </label>
                  <input
                    type="text"
                    placeholder={isTa ? 'பெயர்' : 'Chef Name'}
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#fff'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#cbd5e1', fontWeight: '600', marginBottom: '6px', fontSize: '0.9rem' }}>
                    {isTa ? 'மாநிலம்' : 'State / Region'}
                  </label>
                  <select
                    value={userState}
                    onChange={(e) => setUserState(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: '#0f172a',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#fff'
                    }}
                  >
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Kerala">Kerala</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Punjab">Punjab</option>
                  </select>
                </div>
              </div>

              {/* Portion size & Rating */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label style={{ display: 'block', color: '#cbd5e1', fontWeight: '600', marginBottom: '6px', fontSize: '0.9rem' }}>
                    {isTa ? 'நபர்கள் அளவு' : 'Portions Cooked'}
                  </label>
                  <select
                    value={portionSize}
                    onChange={(e) => setPortionSize(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: '#0f172a',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#fff'
                    }}
                  >
                    <option value="1-2 Persons">1-2 Persons</option>
                    <option value="3-4 Persons">3-4 Persons</option>
                    <option value="5-6 Persons">5-6 Persons</option>
                    <option value="8+ Persons">8+ Persons</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#cbd5e1', fontWeight: '600', marginBottom: '6px', fontSize: '0.9rem' }}>
                    {isTa ? 'மதிப்பீடு (Rating)' : 'Your Rating'}
                  </label>
                  <select
                    value={rating}
                    onChange={(e) => setRating(parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: '#0f172a',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#fbbf24'
                    }}
                  >
                    <option value={5}>⭐⭐⭐⭐⭐ 5 Stars</option>
                    <option value={4}>⭐⭐⭐⭐ 4 Stars</option>
                    <option value={3}>⭐⭐⭐ 3 Stars</option>
                  </select>
                </div>
              </div>

              {/* Caption / Cooking Notes */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#cbd5e1', fontWeight: '600', marginBottom: '6px', fontSize: '0.9rem' }}>
                  {isTa ? 'சமையல் குறிப்பு / கருத்து (Cooking Notes)' : 'Caption / Review / Tips'}
                </label>
                <textarea
                  rows="3"
                  placeholder={isTa ? 'எப்படி இருந்தது? குடும்பத்தினர் விரும்பினார்களா?' : 'How was the taste? Any special kitchen tip?'}
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: 'rgba(15, 23, 42, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Submit Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    background: 'transparent',
                    color: '#cbd5e1',
                    cursor: 'pointer'
                  }}
                >
                  {isTa ? 'ரத்து' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    background: uploading ? '#64748b' : 'linear-gradient(135deg, #10b981, #059669)',
                    color: '#fff',
                    fontWeight: '700',
                    cursor: uploading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {uploading ? (isTa ? 'சேமிக்கப்படுகிறது...' : 'Saving to DB...') : (isTa ? 'PostgreSQL-ல் சேமி' : 'Save to PostgreSQL')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
